import { FC, useEffect, useMemo, useRef } from "react";
import { Field, Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import { TextField } from "shared/ui/ae-user-input";
import clsx from "clsx";
import _debounce from "lodash/debounce";

import { CampaignI } from "@/interfaces/campaign";
import { yupPhone } from "shared/lib/helpers/yup";
import { useLocalStorage } from "@/context/local-storage";
import { AccountI } from "shared/lib/interfaces/account";

const EnhancedTextField = TextField(Field);

export interface CreateNewLeadFormI {
  campaign_name: string;
  account_name?: string;
  first_name: string;
  last_name: string;
  title: string;
  phone: string;
  mobile: string;
  email: string;
  linkedin_url: string;
  // REQUIRED - Company core fields (parity with CSV uploads)
  company_state: string;
  company_industry: string;
  // OPTIONAL - Company location fields
  company_city: string;
  company_country: string;
  company_postal_code: string;
  // OPTIONAL - Person location fields
  person_city: string;
  person_state: string;
  person_country: string;
  person_postal_code: string;
}

interface NewLeadFormContainerPropsI {
  campaign?: CampaignI;
  account?: AccountI;
  loading?: boolean;
  onSubmit?: (data: CreateNewLeadFormI) => void;
  onCancel?: () => void;
}

// Validation schema - company fields only required when creating new account
const getCreateNewLeadFormValidation = (hasExistingAccount: boolean) =>
  Yup.object().shape(
    {
      account_name: Yup.string()
        .min(2, "Minimum 2 characters")
        .max(1000, "Maximum 1000 characters")
        .required("Required field"),
      first_name: Yup.string()
        .max(100, "Maximum 100 characters")
        .required("Required field"),
      last_name: Yup.string()
        .max(100, "Maximum 100 characters")
        .required("Required field"),
      title: Yup.string()
        .max(1000, "Maximum 1000 characters")
        .required("Required field"),
      phone: Yup.string().when("mobile", {
        is: (mobile: string) => !mobile,
        then: yupPhone("phone"),
        otherwise: Yup.string(),
      }),
      mobile: Yup.string().when("phone", {
        is: (phone: string) => !phone,
        then: yupPhone("mobile"),
        otherwise: Yup.string(),
      }),
      email: Yup.string()
        .email("Enter a valid email")
        .required("Required field"),
      linkedin_url: Yup.string(),
      // Company fields - only required when creating new account
      company_state: hasExistingAccount
        ? Yup.string().max(100, "Maximum 100 characters")
        : Yup.string()
            .max(100, "Maximum 100 characters")
            .required("Company HQ State is required"),
      company_industry: hasExistingAccount
        ? Yup.string().max(100, "Maximum 100 characters")
        : Yup.string()
            .max(100, "Maximum 100 characters")
            .required("Company Industry is required"),
      // OPTIONAL - Company location
      company_city: Yup.string().max(100, "Maximum 100 characters"),
      company_country: Yup.string().max(100, "Maximum 100 characters"),
      company_postal_code: Yup.string().max(20, "Maximum 20 characters"),
      // OPTIONAL - Person location
      person_city: Yup.string().max(100, "Maximum 100 characters"),
      person_state: Yup.string().max(100, "Maximum 100 characters"),
      person_country: Yup.string().max(100, "Maximum 100 characters"),
      person_postal_code: Yup.string().max(20, "Maximum 20 characters"),
    },
    // @ts-ignore
    ["mobile", "phone"]
  );

// TODO - Replace that with a more intuitive way to handle form changes. That
// should be considered as an anti-pattern.
const OnFormChange: FC<{ values: CreateNewLeadFormI }> = ({ values }) => {
  const storage = useLocalStorage();

  const debouncedPersistNewLeadForm = useMemo(
    () =>
      _debounce((value: CreateNewLeadFormI) => {
        storage.newLeadModalPersistedValues = value;
      }, 500),
    []
  );

  useEffect(() => {
    debouncedPersistNewLeadForm(values);
  }, [values]);

  return null;
};

export const NewLeadForm: FC<NewLeadFormContainerPropsI> = ({
  campaign,
  account,
  loading,
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const formikRef = useRef<FormikProps<CreateNewLeadFormI>>(null);
  const storage = useLocalStorage();

  // Validation schema depends on whether we have an existing account
  const validationSchema = useMemo(
    () => getCreateNewLeadFormValidation(!!account),
    [account]
  );

  const form: CreateNewLeadFormI = useMemo(
    () => ({
      campaign_name: campaign?.name || "",
      account_name: account?.name || "",
      first_name: "",
      last_name: "",
      title: "",
      phone: "",
      mobile: "",
      email: "",
      linkedin_url: "",
      // Company core fields (only used when creating new account)
      company_state: "",
      company_industry: "",
      // Company location (only used when creating new account)
      company_city: "",
      company_country: "",
      company_postal_code: "",
      // Person location
      person_city: "",
      person_state: "",
      person_country: "",
      person_postal_code: "",
      ...storage.newLeadModalPersistedValues,
    }),
    [campaign, account]
  );

  return (
    <div>
      <Formik
        initialValues={form}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={formikRef}
        enableReinitialize
      >
        {({ errors, touched, values }) => (
          <Form>
            <OnFormChange values={values} />

            <EnhancedTextField
              name="account_name"
              label="Account Name"
              placeholder="Add account name"
              errors={errors.account_name}
              touched={touched.account_name}
              disabled={!!account}
            />

            <EnhancedTextField
              name="first_name"
              label="First Name"
              placeholder="Add first name"
              errors={errors.first_name}
              touched={touched.first_name}
            />

            <EnhancedTextField
              name="last_name"
              label="Last Name"
              placeholder="Add last name"
              errors={errors.last_name}
              touched={touched.last_name}
            />

            <EnhancedTextField
              name="title"
              label="Title"
              placeholder="Add title"
              errors={errors.title}
              touched={touched.title}
            />

            <EnhancedTextField
              name="phone"
              label="Phone Number"
              placeholder="Add phone number"
              errors={errors.phone}
              touched={touched.phone}
            />

            <EnhancedTextField
              name="mobile"
              label="Mobile Number"
              placeholder="Add mobile number"
              errors={errors.mobile}
              touched={touched.mobile}
            />

            <EnhancedTextField
              name="email"
              label="Work Email"
              placeholder="Add email"
              errors={errors.email}
              touched={touched.email}
            />

            <EnhancedTextField
              name="linkedin_url"
              label="Linkedin URL"
              placeholder="Add Linkedin URL"
              errors={errors.linkedin_url}
              touched={touched.linkedin_url}
            />

            {/* Company Info Section - Only show when creating new account */}
            {!account && (
              <div className="mt-4 border-t pt-4">
                <p className="mb-2 text-sm font-semibold text-base-content/60">
                  Company Info
                </p>

                <EnhancedTextField
                  name="company_state"
                  label="Company HQ State *"
                  placeholder="Add company state"
                  errors={errors.company_state}
                  touched={touched.company_state}
                />

                <EnhancedTextField
                  name="company_industry"
                  label="Company Industry *"
                  placeholder="Add company industry"
                  errors={errors.company_industry}
                  touched={touched.company_industry}
                />
              </div>
            )}

            {/* Company Location Section - Only show when creating new account */}
            {!account && (
              <div className="mt-4 border-t pt-4">
                <p className="mb-2 text-sm font-semibold text-base-content/60">
                  Company Location (Optional)
                </p>

                <EnhancedTextField
                  name="company_city"
                  label="Company HQ City"
                  placeholder="Add company city"
                  errors={errors.company_city}
                  touched={touched.company_city}
                />

                <EnhancedTextField
                  name="company_country"
                  label="Company HQ Country"
                  placeholder="Add company country"
                  errors={errors.company_country}
                  touched={touched.company_country}
                />

                <EnhancedTextField
                  name="company_postal_code"
                  label="Company HQ Postal Code"
                  placeholder="Add postal code"
                  errors={errors.company_postal_code}
                  touched={touched.company_postal_code}
                />
              </div>
            )}

            {/* Person Location Section - Optional */}
            <div className="mt-4 border-t pt-4">
              <p className="mb-2 text-sm font-semibold text-base-content/60">
                Person Location (Optional)
              </p>

              <EnhancedTextField
                name="person_city"
                label="Person City"
                placeholder="Add person city"
                errors={errors.person_city}
                touched={touched.person_city}
              />

              <EnhancedTextField
                name="person_state"
                label="Person State"
                placeholder="Add person state"
                errors={errors.person_state}
                touched={touched.person_state}
              />

              <EnhancedTextField
                name="person_country"
                label="Person Country"
                placeholder="Add person country"
                errors={errors.person_country}
                touched={touched.person_country}
              />

              <EnhancedTextField
                name="person_postal_code"
                label="Person Postal Code"
                placeholder="Add postal code"
                errors={errors.person_postal_code}
                touched={touched.person_postal_code}
              />
            </div>

            <div className="mt-8 flex justify-end">
              <button
                type="button"
                className="btn-ae-text mr-2 cursor-pointer items-center px-4 text-black/40"
                onClick={onCancel}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className={clsx("btn-ae-default w-[140px]", {
                  loading,
                })}
              >
                Save
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default NewLeadForm;
